import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useContentStore } from '@/store/contentStore';
import { CARD_TYPES, CARD_TYPE_LABELS, SOURCE_LANGUAGE_LABELS, type CardType, type CreateContentRequest } from '@/types/content';
import { PageHeader } from '@/components/PageHeader';
import { ContentForm, emptyFormValues, type ContentFormValues } from '@/components/ContentForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, Switch } from '@/components/ui/misc';

export function ContentCreate() {
  const navigate = useNavigate();
  const { createCard, generateContent, loading, error, clearError } = useContentStore();

  const [values, setValues] = useState<ContentFormValues>(emptyFormValues);
  const [generateTranslations, setGenerateTranslations] = useState(true);

  const [showAiGenerate, setShowAiGenerate] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const handleSubmit = async () => {
    const data: CreateContentRequest = {
      cardType: values.cardType,
      title: values.title.trim(),
      summary: values.summary.trim() || values.title.trim(),
      body: values.body,
      publishDate: values.publishDate,
      expirationDate: values.expirationDate || undefined,
      isPushFlagged: values.isPushFlagged,
      generateTranslations,
      sourceLanguage: values.sourceLanguage,
      mediaUrls: values.mediaUrls.length > 0 ? values.mediaUrls : undefined,
      externalLinks: values.externalLinks.length > 0 ? values.externalLinks : undefined,
    };

    const card = await createCard(data);
    if (card) {
      toast.success('Card created as a draft');
      navigate(`/content/${card.cardId}`);
    }
  };

  const handleAiGenerate = async () => {
    const card = await generateContent({
      cardType: values.cardType,
      publishDate: values.publishDate,
      prompt: aiPrompt.trim() || undefined,
    });
    if (card) {
      setShowAiGenerate(false);
      toast.success('Generation started. The card opens when the text is ready.');
      navigate(`/content/${card.cardId}`);
    }
  };

  const expirationError =
    values.expirationDate && values.expirationDate < values.publishDate
      ? 'The expiration date must not be before the publish date.'
      : undefined;

  return (
    <div>
      <PageHeader
        title="Create content"
        description="A new card. It is saved as a draft until you publish it."
        backTo="/content"
        actions={
          <Button variant="secondary" onClick={() => setShowAiGenerate(true)}>
            <Sparkles />
            Generate with AI
          </Button>
        }
      />

      {error && (
        <Alert variant="error" title="Could not save" onDismiss={clearError} className="mb-6">
          {error}
        </Alert>
      )}

      <ContentForm
        values={values}
        onChange={setValues}
        onSubmit={handleSubmit}
        submitLabel="Create content"
        submitting={loading}
        errors={{ expirationDate: expirationError }}
        optionsSlot={
          <Card>
            <CardHeader>
              <CardTitle>Translations</CardTitle>
              <CardDescription>
                The 9 other languages are written by Vapor AI from the {SOURCE_LANGUAGE_LABELS[values.sourceLanguage]} text.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
                <span>
                  <span className="block text-sm font-medium">Generate translations</span>
                  <span className="block text-xs text-muted-foreground">Off: the card has {SOURCE_LANGUAGE_LABELS[values.sourceLanguage]} text only.</span>
                </span>
                <Switch checked={generateTranslations} onCheckedChange={setGenerateTranslations} aria-label="Generate translations" />
              </label>
            </CardContent>
          </Card>
        }
      />

      <Dialog open={showAiGenerate} onOpenChange={(open) => { if (!loading) setShowAiGenerate(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate with AI</DialogTitle>
            <DialogDescription>
              Vapor AI writes a card of the chosen type in English for {values.publishDate}. The card is saved as a draft and opens for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ai-type">Card type</Label>
              <Select value={values.cardType} onValueChange={(v) => setValues({ ...values, cardType: v as CardType })}>
                <SelectTrigger id="ai-type" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARD_TYPES.filter(t => t !== 'DALMATIAN_WORD').map(type => (
                    <SelectItem key={type} value={type}>{CARD_TYPE_LABELS[type]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ai-prompt">Topic (optional)</Label>
              <Textarea
                id="ai-prompt"
                className="mt-1.5"
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="For example: a tip about visiting Hvar island"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiGenerate(false)} disabled={loading}>Cancel</Button>
            <Button variant="secondary" onClick={handleAiGenerate} loading={loading}>
              <Sparkles />
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
